// client/src/components/InventoryManager.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Form, Modal, ProgressBar, Badge } from 'react-bootstrap';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function InventoryManager({ addAlert }) {
  const [inventory, setInventory] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUseModal, setShowUseModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newRoll, setNewRoll] = useState({
    design_id: '',
    roll_number: '',
    total_labels: 5000,
    purchase_date: new Date().toISOString().split('T')[0],
    expiry_date: ''
  });
  const [usageData, setUsageData] = useState({
    labels_used: 0,
    purpose: ''
  });

  useEffect(() => {
    fetchInventory();
    fetchDesigns();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/get.php`);
      setInventory(response.data.records || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      addAlert('Failed to load inventory', 'danger');
    }
  };

  const fetchDesigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/designs/read.php`);
      setDesigns(response.data.records || []);
    } catch (error) {
      console.error('Error fetching designs:', error);
    }
  };

  const handleAddRoll = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API_URL}/inventory/add_roll.php`, newRoll);
      addAlert('New roll added successfully!', 'success');
      setShowAddModal(false);
      setNewRoll({
        design_id: '',
        roll_number: '',
        total_labels: 5000,
        purchase_date: new Date().toISOString().split('T')[0],
        expiry_date: ''
      });
      fetchInventory();
    } catch (error) {
      console.error('Error adding roll:', error);
      addAlert('Failed to add roll', 'danger');
    }
  };

  const handleUseLabels = async (e) => {
    e.preventDefault();
    
    if (!selectedItem || usageData.labels_used <= 0) {
      addAlert('Please enter a valid number of labels', 'warning');
      return;
    }

    if (usageData.labels_used > selectedItem.labels_remaining) {
      addAlert('Cannot use more labels than available', 'danger');
      return;
    }

    try {
      const data = {
        id: selectedItem.id,
        labels_used: parseInt(usageData.labels_used),
        purpose: usageData.purpose
      };
      
      await axios.post(`${API_URL}/inventory/update.php`, data);
      addAlert('Labels used updated successfully!', 'success');
      setShowUseModal(false);
      setUsageData({ labels_used: 0, purpose: '' });
      fetchInventory();
    } catch (error) {
      console.error('Error updating inventory:', error);
      addAlert('Failed to update inventory', 'danger');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Active': return <Badge bg="success">Active</Badge>;
      case 'Low': return <Badge bg="warning">Low Stock</Badge>;
      case 'Empty': return <Badge bg="danger">Empty</Badge>;
      case 'Reserved': return <Badge bg="info">Reserved</Badge>;
      default: return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const openUseModal = (item) => {
    setSelectedItem(item);
    setUsageData({ labels_used: 0, purpose: '' });
    setShowUseModal(true);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Inventory Management</h2>
        <Button variant="purple" onClick={() => setShowAddModal(true)}>
          <i className="fas fa-plus me-2"></i>
          Add New Roll
        </Button>
      </div>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Current Inventory</Card.Title>
              {inventory.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-boxes fa-3x mb-3"></i>
                  <p>No inventory yet. Add your first roll!</p>
                </div>
              ) : (
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>Roll #</th>
                      <th>Design</th>
                      <th>SKU</th>
                      <th>Total Labels</th>
                      <th>Used/Remaining</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.roll_number}</strong>
                          <div className="small text-muted">
                            Purchased: {item.purchase_date}
                          </div>
                        </td>
                        <td>
                          <div>{item.design_name}</div>
                          <div className="small text-muted">{item.brand_name}</div>
                        </td>
                        <td>
                          <code>{item.sku_code}</code>
                        </td>
                        <td>{item.total_labels.toLocaleString()}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="me-2">
                              {item.labels_used} / {item.labels_remaining}
                            </div>
                            <div style={{ width: '100px' }}>
                              <ProgressBar 
                                now={(item.labels_remaining / item.total_labels) * 100}
                                variant={
                                  item.labels_remaining <= 1000 ? 'warning' : 
                                  item.labels_remaining <= 0 ? 'danger' : 'success'
                                }
                                className="progress-bar-custom"
                              />
                            </div>
                          </div>
                        </td>
                        <td>{getStatusBadge(item.status)}</td>
                        <td>
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={() => openUseModal(item)}
                            disabled={item.status === 'Empty'}
                          >
                            <i className="fas fa-minus me-1"></i>
                            Use Labels
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add New Roll Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Roll</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddRoll}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Select Design *</Form.Label>
              <Form.Select
                value={newRoll.design_id}
                onChange={(e) => setNewRoll({...newRoll, design_id: e.target.value})}
                required
              >
                <option value="">Choose a design...</option>
                {designs.map((design) => (
                  <option key={design.id} value={design.id}>
                    {design.design_name} ({design.sku_code})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Roll Number *</Form.Label>
              <Form.Control
                type="text"
                value={newRoll.roll_number}
                onChange={(e) => setNewRoll({...newRoll, roll_number: e.target.value})}
                placeholder="e.g., ROLL-001"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Total Labels</Form.Label>
              <Form.Control
                type="number"
                value={newRoll.total_labels}
                onChange={(e) => setNewRoll({...newRoll, total_labels: e.target.value})}
                required
              />
              <Form.Text className="text-muted">
                Standard TVS LP46 roll contains 5000 labels (50mm × 80mm)
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Purchase Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newRoll.purchase_date}
                    onChange={(e) => setNewRoll({...newRoll, purchase_date: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Expiry Date (Optional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={newRoll.expiry_date}
                    onChange={(e) => setNewRoll({...newRoll, expiry_date: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="purple" type="submit">
              <i className="fas fa-save me-2"></i>
              Add Roll
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Use Labels Modal */}
      <Modal show={showUseModal} onHide={() => setShowUseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Use Labels</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUseLabels}>
          <Modal.Body>
            {selectedItem && (
              <>
                <div className="mb-3">
                  <strong>Roll:</strong> {selectedItem.roll_number}<br />
                  <strong>Design:</strong> {selectedItem.design_name}<br />
                  <strong>Remaining Labels:</strong> {selectedItem.labels_remaining}
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Number of Labels to Use *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max={selectedItem.labels_remaining}
                    value={usageData.labels_used}
                    onChange={(e) => setUsageData({...usageData, labels_used: e.target.value})}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Purpose / Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={usageData.purpose}
                    onChange={(e) => setUsageData({...usageData, purpose: e.target.value})}
                    placeholder="e.g., Production batch, Sample printing, etc."
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUseModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              <i className="fas fa-check me-2"></i>
              Record Usage
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default InventoryManager;